import React, { useState } from 'react';
import classnames from 'classnames';
import { useEffect } from 'react';
import { CloseIcon, ErrorIcon, SuccessIcon, WarningIcon } from '../icons/icons';
import './oe-notification.scss';
import DefaultButton from '../default-button/default-button';

interface IPopupProps {
    message: string | JSX.Element;
    statusCode?: number;
    alignment?: string;
    withCloseIcon?: boolean;
    withFade?: boolean;
    buttonText?: string;
    buttonHandler?: () => void;
    className?: string;
    noStatusIcon?: boolean;
}

interface IProps {
    popupProps: IPopupProps;
}

export enum ResponseCode {
    success = 200,
    warning = 300,
    error = 400
}

enum NotificationStatus {
    success = 'success',
    warning = 'warning',
    error = 'error'
}

const ADJUSTED_TIME_FOR_FADE_OUT_ANIMATION = 1400;
const NOTIFICATION_DISPLAY_TIME = 3000;

const OENotification = (props: IProps) => {
    const {
        message,
        alignment = 'bottom',
        statusCode = ResponseCode.success,
        withCloseIcon,
        withFade = true,
        buttonText,
        buttonHandler,
        className
    } = props.popupProps;

    const [openOENotification, setOENotification] = useState(true);
    const [fadeOut, setFadeOut] = useState(false);

    let notificationStatus = NotificationStatus.success;
    if (statusCode >= ResponseCode.warning && statusCode < ResponseCode.error) {
        notificationStatus = NotificationStatus.warning;
    } else if (statusCode >= ResponseCode.error) {
        notificationStatus = NotificationStatus.error;
    }

    const parentClassName = classnames(`oe-notification ${className}`, {
        'oe-notification--fade-in': withCloseIcon,
        'oe-notification--fade-out': fadeOut,
        'align__left': alignment === 'left',
        'align__right': alignment === 'right',
        'align__top': alignment === 'top',
        'align__bottom': alignment === 'bottom'
    });

    const fadeOutThenClose = () => {
        setFadeOut(true);
        setTimeout(() => {
            setOENotification(false);
        }, ADJUSTED_TIME_FOR_FADE_OUT_ANIMATION);
    };

    const containerClassName = classnames('oe-notification__container', {
        'success': notificationStatus === NotificationStatus.success,
        'warning': notificationStatus === NotificationStatus.warning,
        'error': notificationStatus === NotificationStatus.error
    });

    useEffect(() => {
        if (withFade && !withCloseIcon) setTimeout(fadeOutThenClose, NOTIFICATION_DISPLAY_TIME);
    }, [withFade, withCloseIcon]);

    const handleClose = e => {
        e?.stopPropagation();
        setOENotification(false);
    };

    const getNotificationIcon = () => {
        if (props.popupProps.noStatusIcon) {
            return null;
        }

        let notificationIcon;
        switch (notificationStatus) {
            case NotificationStatus.error:
                notificationIcon = <ErrorIcon/>;
                break;
            case NotificationStatus.warning:
                notificationIcon = <WarningIcon/>;
                break;
            case NotificationStatus.success:
                notificationIcon = <SuccessIcon/>;
                break;
        }
        return notificationIcon;
    };

    return (
        openOENotification ? <div className={parentClassName}>
                <div className={containerClassName}>
                    {getNotificationIcon()}
                    <p>{message}</p>
                    {buttonText && <DefaultButton
                        modifier='slim'
                        text={buttonText}
                        onClick={buttonHandler}
                    />}
                    {withCloseIcon && <CloseIcon className='oe-notification--close-icon' onClick={e => handleClose(e)}/>}
                </div>
            </div>
            : null
    );
};

export default OENotification;
